import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { apiGet, apiPost } from '@/api/client';
import { ConflictError } from '@/api/errors';
import type { CompanyMember, PlatformUser } from '@/api/types';
import { AsyncSection } from '@/app/components/AsyncSection';
import { SubmitError } from '@/app/components/SubmitError';
import { Alert } from '@/app/components/ui/Alert';
import { Button } from '@/app/components/ui/Button';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { EmptyState } from '@/app/components/ui/EmptyState';
import { Field, FormActions, Input, Select } from '@/app/components/ui/Field';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { useMe } from '@/features/me/MeContext';
import { PreferredLanguageSelect } from '@/i18n/PreferredLanguageSelect';
import { useAsync } from '@/lib/useAsync';
import { useSubmit } from '@/lib/useSubmit';

interface CreatedPerson {
  user_id?: string;
  has_login?: boolean;
  invite_sent?: boolean;
  warning?: string;
}

/**
 * Provisioning: POST /users into exactly one scope. With an e-mail address
 * the person gets a Keycloak account and an invitation mail; without one, a
 * login-less identity (the kind an invite can upgrade later). A 409 means the
 * address already belongs to an identity — the API sends its user_id back so
 * the caller can add that person instead of creating a duplicate.
 */
function AddPersonForm({
  scope,
  backTo,
}: {
  scope: { company_id: string } | { department_id: string };
  backTo: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [nickname, setNickname] = useState('');
  const [language, setLanguage] = useState('');
  const [region, setRegion] = useState('');
  const [result, setResult] = useState<CreatedPerson | null>(null);
  // Gender, birthdate, height and weight are deliberately absent: creating a
  // person is writing somebody else's record, and the API refuses those four
  // here whoever asks. They are the person's own to fill in once they sign in.
  const form = useSubmit(async () => {
    const created = (await apiPost('/users', {
      body: {
        ...scope,
        ...(name.trim() !== '' ? { name } : {}),
        ...(email.trim() !== '' ? { email } : {}),
        ...(nickname.trim() !== '' ? { nickname } : {}),
        ...(language.trim() !== '' ? { preferred_language: language } : {}),
        ...(region.trim() !== '' ? { region } : {}),
      },
    })) as CreatedPerson | undefined;
    setResult(created ?? null);
  });

  if (result) {
    return (
      <div role="status">
        <Card className="max-w-2xl">
          <p className="text-ink text-sm">
            {t('users.addPerson.created')}{' '}
            {result.invite_sent
              ? t('users.addPerson.inviteSent')
              : result.has_login
                ? t('users.addPerson.alreadyHasLogin')
                : t('users.addPerson.noLoginYet')}
          </p>
          {result.warning && <p className="text-warning mt-2 text-[13px]">{result.warning}</p>}
          <FormActions className="mt-4">
            <Button onClick={() => void navigate(backTo)}>{t('users.addPerson.done')}</Button>
          </FormActions>
        </Card>
      </div>
    );
  }

  return (
    <Card className="max-w-2xl">
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void form.submit();
        }}
      >
        <Field label={t('users.addPerson.name')}>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={t('users.addPerson.email')} hint={t('users.addPerson.emailHint')}>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label={t('users.addPerson.nickname')}>
          <Input value={nickname} onChange={(e) => setNickname(e.target.value)} />
        </Field>
        <Field label={t('profile.preferredLanguageOptional')}>
          <PreferredLanguageSelect value={language} onChange={setLanguage} />
        </Field>
        <Field label={t('users.addPerson.region')}>
          <Input
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            placeholder="BE"
            maxLength={2}
          />
        </Field>
        {form.error instanceof ConflictError ? (
          <Alert tone="danger">
            {t('users.addPerson.conflict', {
              reason: form.error.reason ? ` (${form.error.reason})` : '',
            })}
          </Alert>
        ) : (
          <SubmitError error={form.error} />
        )}
        <FormActions>
          <Button as={Link} to={backTo} variant="ghost">
            {t('users.addPerson.cancel')}
          </Button>
          <Button type="submit" disabled={form.pending}>
            {t('users.addPerson.create')}
          </Button>
        </FormActions>
      </form>
    </Card>
  );
}

export function CompanyAddPersonPage() {
  const { t } = useTranslation();
  const { companyId = '' } = useParams();
  const { permissions } = useMe();
  const backTo = `/manage/companies/${companyId}`;

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('users.addPerson.companyTitle')} />
      <Card className="max-w-2xl" aria-label={t('users.addPerson.newPersonCardTitle')}>
        <CardHeader title={t('users.addPerson.newPersonCardTitle')} />
        <AddPersonForm scope={{ company_id: companyId }} backTo={backTo} />
      </Card>
      {permissions.canProvisionInCompany(companyId) && (
        <ExistingUserForm companyId={companyId} backTo={backTo} />
      )}
    </section>
  );
}

/**
 * The company-level counterpart of `ExistingMemberForm` below: a person who
 * already exists on the platform (in another company, or in none at all)
 * but isn't yet a member of *this* one. Unlike the department form, there is
 * no pre-scoped candidate list to pick from — `GET /users/all` is the one
 * endpoint that isn't limited by affiliation, so candidates are searched
 * from the full platform list and narrowed client-side.
 */
function ExistingUserForm({ companyId, backTo }: { companyId: string; backTo: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const platformUsers = useAsync(
    () => apiGet('/users/all') as unknown as Promise<PlatformUser[]>,
    [],
  );
  const [query, setQuery] = useState('');
  const [userId, setUserId] = useState('');
  const form = useSubmit(async () => {
    await apiPost('/companies/{companyId}/users', {
      params: { companyId },
      body: { user_id: userId },
    });
    await navigate(backTo);
  });

  return (
    <Card className="max-w-2xl" aria-label={t('users.addPerson.existingPersonCardTitle')}>
      <CardHeader title={t('users.addPerson.existingPersonCardTitle')} />
      <AsyncSection state={platformUsers} label={t('users.addPerson.existingPersonCardTitle')}>
        {(users) => (
          <ExistingUserFormBody
            users={users}
            companyId={companyId}
            query={query}
            setQuery={setQuery}
            userId={userId}
            setUserId={setUserId}
            form={form}
          />
        )}
      </AsyncSection>
    </Card>
  );
}

function ExistingUserFormBody({
  users,
  companyId,
  query,
  setQuery,
  userId,
  setUserId,
  form,
}: {
  users: PlatformUser[];
  companyId: string;
  query: string;
  setQuery: (value: string) => void;
  userId: string;
  setUserId: (id: string) => void;
  form: { pending: boolean; error: Error | null; submit: () => Promise<boolean> };
}) {
  const { t } = useTranslation();

  // `company_ids` is optional on the wire (per the API spec) even though the
  // hand-written `PlatformUser` type claims it's always present — an
  // unaffiliated identity can arrive without the field at all, not just as `[]`.
  const eligible = useMemo(
    () => users.filter((u) => !(u.company_ids ?? []).includes(companyId)),
    [users, companyId],
  );

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === '') return eligible;
    return eligible.filter(
      (u) => u.name?.toLowerCase().includes(needle) || u.email?.toLowerCase().includes(needle),
    );
  }, [eligible, query]);

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        void form.submit();
      }}
    >
      <Field label={t('users.addPerson.searchPeople')}>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('users.addPerson.searchPlaceholder')}
        />
      </Field>
      <Field label={t('users.addPerson.person')}>
        <Select value={userId} onChange={(e) => setUserId(e.target.value)} required>
          <option value="">{t('users.addPerson.choose')}</option>
          {matches.map((u) => (
            <option key={u.user_id} value={u.user_id}>
              {u.name ?? u.email ?? u.user_id}
              {u.email && u.name ? ` (${u.email})` : ''}
            </option>
          ))}
        </Select>
      </Field>
      {matches.length === 0 && <EmptyState text={t('users.addPerson.noEligible')} />}
      {form.error instanceof ConflictError ? (
        <Alert tone="danger">{t('users.addPerson.editionMismatch')}</Alert>
      ) : (
        <SubmitError error={form.error} />
      )}
      <FormActions>
        <Button type="submit" disabled={form.pending || userId === ''}>
          {t('users.addPerson.addToCompany')}
        </Button>
      </FormActions>
    </form>
  );
}

/**
 * The department page also offers adding an *existing* company member —
 * that path (POST /departments/{id}/users) belongs to the company admin,
 * while creating a brand-new person is open to the department admin too.
 */
export function DepartmentAddPersonPage() {
  const { t } = useTranslation();
  const { departmentId = '' } = useParams();
  const { permissions } = useMe();
  const department = useAsync(
    () => apiGet('/departments/{departmentId}', { params: { departmentId } }),
    [departmentId],
  );
  const backTo = `/departments/${departmentId}`;

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('users.addPerson.departmentTitle')} />
      <AsyncSection state={department} label={t('departments.detail.thisDepartment')}>
        {(data) => {
          const companyId = data.company_id ?? null;
          const mayAddExisting = companyId !== null && permissions.canManageDepartments(companyId);
          return (
            <>
              <Card className="max-w-2xl" aria-label={t('users.addPerson.newPersonCardTitle')}>
                <CardHeader title={t('users.addPerson.newPersonCardTitle')} />
                <AddPersonForm scope={{ department_id: departmentId }} backTo={backTo} />
              </Card>
              {mayAddExisting && (
                <ExistingMemberForm companyId={companyId} departmentId={departmentId} />
              )}
            </>
          );
        }}
      </AsyncSection>
    </section>
  );
}

function ExistingMemberForm({
  companyId,
  departmentId,
}: {
  companyId: string;
  departmentId: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const members = useAsync(
    () =>
      apiGet('/companies/{companyId}/users', {
        params: { companyId },
      }) as unknown as Promise<CompanyMember[]>,
    [companyId],
  );
  const [userId, setUserId] = useState('');
  const form = useSubmit(async () => {
    await apiPost('/departments/{departmentId}/users', {
      params: { departmentId },
      body: { user_id: userId },
    });
    await navigate(`/departments/${departmentId}`);
  });

  return (
    <Card className="max-w-2xl" aria-label={t('users.addPerson.existingCardTitle')}>
      <CardHeader title={t('users.addPerson.existingCardTitle')} />
      <AsyncSection state={members} label={t('users.addPerson.existingCardTitle')}>
        {(rows) => (
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              void form.submit();
            }}
          >
            <Field label={t('users.addPerson.member')}>
              <Select value={userId} onChange={(e) => setUserId(e.target.value)} required>
                <option value="">{t('users.addPerson.choose')}</option>
                {rows.map((member) => (
                  <option key={member.user_id} value={member.user_id}>
                    {member.name}
                  </option>
                ))}
              </Select>
            </Field>
            <SubmitError error={form.error} />
            <FormActions>
              <Button type="submit" disabled={form.pending || userId === ''}>
                {t('users.addPerson.addToDepartment')}
              </Button>
            </FormActions>
          </form>
        )}
      </AsyncSection>
    </Card>
  );
}
